import { memo, useEffect, useState } from "react";
import { useI18n } from "@/i18n";
import { generateQRDataUrl, downloadQRImage, printQRLabel } from "@/utils/qrGenerator";
import { typography, transitions } from "@/design-system/tokens";

interface QRModalProps {
  code: string;
  label: string;
  onClose: () => void;
}

export const QRModal = memo(function QRModal({ code, label, onClose }: QRModalProps) {
  const { t } = useI18n();
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  useEffect(() => {
    generateQRDataUrl(code, 280).then(setDataUrl);
  }, [code]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "24px" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "20px", padding: "36px", width: "100%", maxWidth: "360px", display: "flex", flexDirection: "column", alignItems: "center", gap: "20px" }}>
        {/* QR Image */}
        {dataUrl ? (
          <img src={dataUrl} alt={code} style={{ width: "220px", height: "220px", borderRadius: "8px" }} />
        ) : (
          <div style={{ width: "220px", height: "220px", background: "#f5f5f5", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#aaa" }}>...</span>
          </div>
        )}

        {/* Code + Label */}
        <div style={{ textAlign: "center" }}>
          <div style={{ fontFamily: typography.fontFamily, fontSize: typography.size.base, fontWeight: typography.weight.bold, color: "#111" }}>{code}</div>
          <div style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#888", marginTop: "4px" }}>{label}</div>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: "10px", width: "100%" }}>
          <button
            onClick={() => { if (dataUrl) downloadQRImage(dataUrl, code); }}
            disabled={!dataUrl}
            style={{ flex: 1, fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#555", background: "#fff", border: "1px solid #e0e0e0", borderRadius: "10px", padding: "11px 0", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", transition: `all ${transitions.fast}` }}
            onMouseEnter={e => { const el = e.currentTarget; el.style.borderColor = "#111"; el.style.color = "#111"; }}
            onMouseLeave={e => { const el = e.currentTarget; el.style.borderColor = "#e0e0e0"; el.style.color = "#555"; }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
            {t.reports.download}
          </button>
          <button
            onClick={() => { if (dataUrl) printQRLabel(dataUrl, code, label); }}
            disabled={!dataUrl}
            style={{ flex: 1, fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#fff", background: "#111", border: "none", borderRadius: "10px", padding: "11px 0", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", transition: `background ${transitions.fast}` }}
            onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.background = "#333")}
            onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.background = "#111")}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>
            Imprimir
          </button>
        </div>

        {/* Close */}
        <button onClick={onClose} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#bbb", background: "transparent", border: "none", cursor: "pointer", marginTop: "-4px" }}>
          {t.planLimit.close}
        </button>
      </div>
    </div>
  );
});
